<template>
  <div class="submenu-page">
    <a-menu
      class="menu"
      :selectedKeys="[currentMenu]"
      @update:selectedKeys="updateMenu"
      mode="inline"
    >
      <template v-for="menu in menus" :key="menu.key">
        <a-menu-item :key="menu.key" v-if="!menu.needPermissions">{{ menu.name }}</a-menu-item>
      </template>
    </a-menu>

    <TabsContainer v-if="currentMenuData?.tabs" :tabs="currentMenuData.tabs" class="main">
      <template v-for="tab in currentMenuData?.tabs" #[tab.key]>
        <slot :name="tab.key" />
      </template>
    </TabsContainer>

    <!-- <platform-settings-template /> -->
    <MainContainer v-else class="main">
      <template v-for="section in currentMenuData?.sections" :key="section.title">
        <div
          :id="`section-${section.key}`"
          class="content-wrapper"
          :class="{ 'whole-page': currentMenuData?.wholePage }"
          v-if="!section.needPermissions"
        >
          <h2 v-if="!section?.hideTitle" class="title">
            <template v-if="section?.icon">
              <img
                v-if="typeof section?.icon === 'string'"
                :src="section?.icon"
                :alt="`${section.title}-icon`"
                class="icon icon__img"
              />
              <component v-else :is="section?.icon" class="icon icon__component" />
            </template>

            {{ section?.title }}

            <a-switch
              v-if="section?.onActiveChange"
              :checked="section.isActive"
              :disabled="restringedPlanConfig(section)"
              size="small"
              style="margin-left: 8px"
              @update:checked="section.onActiveChange"
            />

            <a-tooltip v-if="section?.tooltip" :title="section?.tooltip">
              <InfoCircleOutlined />
            </a-tooltip>

            <a-tag class="text" v-if="section.tag">
              {{ section.tag.label }}

              <a-tooltip v-if="section.tag.tooltip" :title="section.tag.tooltip">
                <InfoCircleOutlined />
              </a-tooltip>
            </a-tag>

            <a-tooltip
              v-if="restringedPlanConfig(section)"
              :title="planTagTooltip(section)"
              placement="top"
            >
              <span class="plan-tag-trigger">
                <a-tag
                  class="plan-tag"
                  :class="`plan-tag--${section.planRequired === 'mcp_token' && isMcpUpgradePlan ? 'upgrade' : 'personalize'}`"
                  @click="openRestrictionModal(section)"
                >
                  {{ section.planRequired === 'mcp_token' && isMcpUpgradePlan ? 'Upgrade' : 'Personalizar' }}
                </a-tag>
              </span>
            </a-tooltip>
          </h2>

          <a-divider v-if="!section?.hideTitle" class="divider" />

          <div
            class="section"
            :class="{ 'section--margin': Number(currentMenuData?.sections?.length) > 1 }"
          >
            <slot :name="section.key" />
          </div>
        </div>
      </template>
    </MainContainer>

    <RestrictionChangePlanModal
      :title="!showAlertRestriction && isMcpUpgradePlan
        ? 'Realiza un upgrade y accede a todo el poder de Beex.'
        : 'Obtén esta funcionalidad personalizando tu plan.'
      "
      v-if="openRestriction"
      v-model:open="openRestriction"
      :planRequest="dataPlan.plan"
      :showAlert="showAlertRestriction"
      :advisorOnly="advisorOnlyRestriction"
      :requestButtonLabel="!showAlertRestriction ? (isMcpUpgradePlan ? 'Upgrade All in One' : 'Personalizar plan') : ''"
    >
      <template #description v-if="!showAlertRestriction && isMcpUpgradePlan">
        Tu plan {{ PlanTypeText[dataPlan.plan as PlanType] }} no incluye esta funcionalidad. <br/>
        Solicita un upgrade y potencia tu ambiente de trabajo. 
        ¡Que tu viaje no se detenga!
      </template>
      <template #body v-else>
        <p>
          Tu plan {{ PlanTypeText[dataPlan.plan as PlanType] }} no incluye esta funcionalidad.<br/>
          Solicita personalizar tu plan para potenciar tu ambiente de trabajo.
        </p>
        <p>
          {{
            isClientBeexcc 
            ? "Cuenta con nosotros para elevar tu experiencia. Estamos listos para asistirte. ✨"
            : "Aprovecha al máximo todo lo que puedes lograr con BeexCC. ¡Lleva tu experiencia más lejos! ✨"
          }}
        </p>
      </template>
    </RestrictionChangePlanModal>
  </div>
</template>

<script setup lang="ts" generic="T, U, V extends string | number">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { SubmenuData } from "@/@types/shared/pickers";

import MainContainer from "@/app/shared/components/organisms/MainContainer.vue";
import TabsContainer from "@/app/shared/components/organisms/TabsContainer.vue";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { PlanType, PlanTypeText } from "@/app/common/types";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps<{
  menus: SubmenuData<T, U, V>[];
  currentMenu: T;
  dataPlan?: any;
}>();

const emit = defineEmits<{
  "update:currentMenu": [newValue: T];
}>();

const router = useRouter();
const route = useRoute();

const openRestriction = ref(false);
const showAlertRestriction = ref(true);
const advisorOnlyRestriction = ref(false);
const PlanEspecial = 5;

function isMenuType(key: unknown): key is T {
  return props.menus.some((m) => m.key === key);
}

const hasBeexApi = computed(() => {
  return props.dataPlan?.configuration?.beex_api?.active;
});

const hasMcpIntegration = computed(() => {
  return props.dataPlan?.configuration?.api_cloud_token?.active;
});

const user = computed(() => store.getters["getDataUser"]);
const isClientBeexcc = computed(() => user.value?.is_beexcc);
const isMcpUpgradePlan = computed(() => (![PlanType.AllInOne, PlanEspecial].includes(props.dataPlan?.plan)));

const restringedPlanConfig = (section: any) => {
  return (
    (section.planRequired == 'beex_api' && !hasBeexApi.value) ||
    (section.planRequired == 'mcp_token' && !hasMcpIntegration.value)
  );
}

const openRestrictionModal = (section: any) => {
  showAlertRestriction.value = section.planRequired !== 'mcp_token';
  // El botón "Personalizar plan" pertenece solo al flujo de upgrade. En las variantes de
  // Personalizar (plan Especial o All in One) la única acción es contactar a un asesor.
  advisorOnlyRestriction.value = section.planRequired === 'mcp_token' && !isMcpUpgradePlan.value;
  openRestriction.value = true;
};

/**
 * Motivo por el que el plan no tiene acceso a la sección. Solo la sección de APIs v2 / MCP
 * se resuelve "consumiendo API calls"; en las de `beex_api` el motivo es otro y no lleva
 * tooltip, así que se devuelve `undefined` y el Tooltip no se abre.
 */
const planTagTooltip = (section: any): string | undefined => {
  if (section.planRequired !== 'mcp_token') return undefined;

  return isMcpUpgradePlan.value
    ? 'Solicita un upgrade de tu plan para consumir las API calls.'
    : 'Solicita personalizar tu plan para consumir las API calls.';
};

watch(
  () => props.currentMenu,
  (newMenu) => {
    router.push({ query: { ...route.query, key: String(newMenu) } });
  },
  { immediate: true }
);

watch(
  () => route.query.key,
  (newQueryKey) => {
    if (isMenuType(newQueryKey)) emit("update:currentMenu", newQueryKey);
  }
);

onMounted(() => {
  const queryMenuKey = route.query.key;

  if (!queryMenuKey) return;

  if (isMenuType(queryMenuKey)) emit("update:currentMenu", queryMenuKey);
});

const updateMenu = (newValue: T[]) => emit("update:currentMenu", newValue[0]);

const currentMenuData = computed(() => props.menus.find((m) => m.key === props.currentMenu));
</script>
<style lang="sass" scoped>
$padding-border: 20px

.submenu-page
  display: grid
  height: 100%
  grid-template-columns: 220px minmax(0, 1fr)

.menu
  margin: 12px 0 !important
  padding-top: 10px !important
  border: 1px solid $gray-5 !important
  border-left: none !important
  border-radius: 0 10px 10px 0
  // flex: 0 0 220px

.content-wrapper
  display: flex
  flex-direction: column

  &.whole-page
    flex: 1 1 auto

.main
  flex: 1 1 auto
  padding: 20px
  overflow-y: scroll

  .title
    font-size: 20px
    font-weight: 600
    line-height: 24px
    margin: 0
    display: flex
    align-items: center
    padding: 0 $padding-border
    .ant-tag
      font-weight: 400
      margin-left: 7px
    .text
      background-color: $gray-2
      font-weight: 600
      font-size: $font-small
      border-radius: 4px
      border: 2px solid $gray-4
      margin-left: 7px
      padding: 0 5px 0

.plan-tag-trigger
  display: inline-flex
  vertical-align: middle

.plan-tag
  cursor: pointer

  &--personalize
    background: #E6FFFB
    border: 1px solid #87E8DE
    color: #08979C

  &--upgrade
    background: #F9F0FF
    border: 1px solid #D3ADF7
    color: #722ED1

.divider
  margin: 16px 0
  background-color: $gray-4

.section
  padding: 0 $padding-border
  flex: 1 1 auto

  &--margin
    margin-bottom: 32px

.icon
  margin-right: 8px

  &__component
    font-size: 20px

  &__img
    height: 24px
</style>
